import { Injectable } from '@angular/core';
import { SideDetailViewService } from '@y42/core/container-with-side-detail-view';
import { AbstractSimpleListViewService } from '@y42/core/list-view';
import { DataTest, DataTestService, DataTestType } from '@y42/data-test';
import { DatasourceTableQuery } from '@y42/datasource';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { takePromise } from '@y42/util/take-promise';
import { wait } from '@y42/util/wait';
import groupBy from 'lodash/groupBy';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  DataTestBuilderSaveOutput,
  DataTestBuilderSidebarInput,
} from '../../data-test-builder/data-test-builder.model';
import { SIDENAV_CONTENT_RENDER_DELAY_MS } from '../../model';
import { JobTestsHttpModel } from '../../model/job.model';
import { DataTestDialogStateService } from '../data-test-dialog/data-test-dialog-state.service';

// "id" is needed for y42-list-view to calculate number of entries correctly
export type DataTestSetupListUIModel = DataTest & { id: string } & {
  latestJob: Pick<JobTestsHttpModel['result'], 'status' | 'error_messages'> | null;
};

@Injectable()
export class DataTestSetupListService extends AbstractSimpleListViewService<DataTestSetupListUIModel> {
  constructor(
    private readonly sideDetailViewService: SideDetailViewService<
      DataTestBuilderSaveOutput,
      DataTestBuilderSidebarInput
    >,
    private readonly dataTestService: DataTestService,
    private readonly dataTestDialogStateService: DataTestDialogStateService,
    private readonly datasourceTableQuery: DatasourceTableQuery,
  ) {
    super();
  }

  /**
   * functions that find a corresponding test in the job run results by the DataTest in the test definition
   */
  private readonly findCorrespondingDataTestDef: Record<
    DataTestType,
    (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => JobTestsHttpModel | null
  > = {
    [DataTestType.Schema]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.Schema) {
        return null;
      }
      // only 1 schema test can exist. choose the fist from the list
      return jobResultTests?.find((jobTest) => jobTest.request.type === DataTestType.Schema) ?? null;
    },
    [DataTestType.Column]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.Column) {
        return null;
      }
      return (
        jobResultTests?.find(
          (jobTest) =>
            jobTest.request.type === DataTestType.Column &&
            jobTest.request.column === dataTest.column &&
            areArraysEqual(jobTest.request.column_conditions.operations, dataTest.column_conditions.operations),
        ) ?? null
      );
    },
    [DataTestType.LowCode]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.LowCode) {
        return null;
      }
      return (
        jobResultTests?.find(
          (jobTest) =>
            jobTest.request.type === DataTestType.LowCode &&
            (jobTest.request.low_code_string ?? '').trim() === (dataTest.low_code_string ?? '').trim(),
        ) ?? null
      );
    },
    [DataTestType.Unique]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.Unique) {
        return null;
      }
      return (
        jobResultTests?.find(
          (jobTest) => jobTest.request.type === DataTestType.Unique && jobTest.request.column === dataTest.column,
        ) ?? null
      );
    },
    [DataTestType.NumRowDrift]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.NumRowDrift) {
        return null;
      }
      return (
        jobResultTests?.find(
          (jobTest) =>
            jobTest.request.type === DataTestType.NumRowDrift &&
            jobTest.request.row_change.threshold_type === dataTest.row_change.threshold_type,
        ) ?? null
      );
    },
    [DataTestType.ReferentialIntegrity]: (dataTest: DataTest, jobResultTests?: JobTestsHttpModel[]) => {
      if (dataTest.type !== DataTestType.ReferentialIntegrity) {
        return null;
      }
      console.error('[Data-test] Referential integrity is not implemented');
      return (
        jobResultTests?.find(
          (jobTest) =>
            jobTest.request.type === DataTestType.ReferentialIntegrity && jobTest.request.column === dataTest.column,
        ) ?? null
      );
    },
  };

  private readonly dataSourceTable$ = this.dataTestDialogStateService.path$.pipe(
    switchMap((path) => this.datasourceTableQuery.selectUIEntity(path)),
  );

  private readonly latestJobTests$: Observable<{
    [T in DataTestType]?: Array<JobTestsHttpModel & { request: { type: T } }>;
  }> = this.dataSourceTable$.pipe(
    map((table) => {
      const testsInJob = table?.latest_job?.tests ?? [];
      const result = groupBy(testsInJob, (item) => item.request.type);
      return result;
    }),
  );

  readonly dataTests$ = this.dataSourceTable$.pipe(map((uiEntity) => uiEntity?.tests?.tests ?? []));
  readonly data$: Observable<DataTestSetupListUIModel[]> = combineLatest([this.dataTests$, this.latestJobTests$]).pipe(
    map(([tests, latestJobTests]) => {
      return tests.map((test, index) => {
        const latestJobCorrespondingDataTest = this.findCorrespondingDataTest(test, latestJobTests);
        return this.toUIModel(test, index, latestJobCorrespondingDataTest);
      });
    }),
  );

  readonly loading$: Observable<boolean> = of(false);

  override async getAll(): Promise<void> {
    // data fetched elsewhere
  }

  override async add(): Promise<void> {
    await this.openSidebar({
      newTest: true,
      dataTests: await firstValueFrom(this.dataTests$),
    });
  }

  async cellClick(rowIndex: number): Promise<void> {
    if (rowIndex < 0) {
      console.error('[Data-test] invalid parameter rowIndex');
      return;
    }
    await this.openSidebar({ editIndex: rowIndex, dataTests: await takePromise(this.dataTests$) });
  }

  async deleteDataTest(rowIndex: number): Promise<void> {
    const tests = [...(await takePromise(this.dataTests$))];
    tests.splice(rowIndex, 1);
    await this.dataTestService.update({ tests, datasourceTablePath: this.dataTestDialogStateService.getPath() });
  }

  private async openSidebar(data: DataTestBuilderSidebarInput): Promise<void> {
    const sidebarData = await this.sideDetailViewService.open(data);

    if (isNullish(sidebarData)) {
      return;
    }

    // wait for the sidebar smooth animation, then save to FS (noticeable on slow CPUs)
    await wait(SIDENAV_CONTENT_RENDER_DELAY_MS);

    await this.dataTestService.update({
      tests: sidebarData.dataTests,
      datasourceTablePath: this.dataTestDialogStateService.getPath(),
    });
  }

  private toUIModel(
    dataTest: DataTest,
    index: number,
    jobDataTest: JobTestsHttpModel | null,
  ): DataTestSetupListUIModel {
    const result: DataTestSetupListUIModel = {
      ...dataTest,
      id: index.toString(),
      latestJob: isNotNullish(jobDataTest)
        ? {
            status: jobDataTest.result.status,
            error_messages: jobDataTest.result.error_messages,
          }
        : null,
    };
    return result;
  }

  private findCorrespondingDataTest(
    dataTest: DataTest,
    jobResultTests: {
      [T in DataTestType]?: JobTestsHttpModel[];
    },
  ): JobTestsHttpModel | null {
    return this.findCorrespondingDataTestDef[dataTest.type](dataTest, jobResultTests[dataTest.type]);
  }
}
